import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'conversation.funnel_updated': this.onConversationFunnelUpdated,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'whatsapp.qrcode_updated': this.onWhatsappQrCodeUpdated,
      'whatsapp.connection_update': this.onWhatsappConnectionUpdate,
      // Chat Rooms events
      'chat_room_message.created': this.onChatRoomMessageCreated,
      'chat_room_message.updated': this.onChatRoomMessageUpdated,
      'chat_room.deleted': this.onChatRoomDeleted,
      'chat_room.member_added': this.onChatRoomMemberAdded,
      'chat_room.member_removed': this.onChatRoomMemberRemoved,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
    // Mensagem recebida pode criar uma não lida: atualiza o contador da aba
    if (data.message_type === MESSAGE_TYPE.INCOMING) {
      this.fetchConversationStats();
    }
  };

  onConversationFunnelUpdated = data => {
    this.app.$store.dispatch('applyFunnelMappingUpdate', data);
    // KanbanView recarrega as colunas quando o evento é do funil aberto
    window.dispatchEvent(
      new CustomEvent('funnel-realtime-update', { detail: data })
    );
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onWhatsappQrCodeUpdated = data => {
    emitter.emit(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onWhatsappConnectionUpdate = data => {
    emitter.emit(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  // ============================================
  // Chat Rooms Event Handlers
  // ============================================

  onChatRoomMessageCreated = data => {
    const roomId = data.conversation_id;
    const currentUserId = this.app.$store.getters.getCurrentUserID;

    // Add message to store
    this.app.$store.commit('chatRooms/ADD_MESSAGE', {
      roomId,
      message: data,
    });

    // Update room's last_message_at
    this.app.$store.commit('chatRooms/SET_CHAT_ROOM_ITEM', {
      id: roomId,
      last_message_at: new Date(data.created_at * 1000).toISOString(),
    });

    // Increment unread count if message is from another user
    if (data.sender?.id !== currentUserId) {
      this.app.$store.commit('chatRooms/INCREMENT_UNREAD_COUNT', roomId);
    }

    // Emit event for components listening via emitter
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MESSAGE_CREATED, data);
  };

  onChatRoomMessageUpdated = data => {
    const roomId = data.conversation_id;

    // Update message in store
    this.app.$store.commit('chatRooms/UPDATE_MESSAGE', {
      roomId,
      message: data,
    });

    // Emit event for components
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MESSAGE_UPDATED, data);
  };

  onChatRoomDeleted = data => {
    const roomId = data.id;

    // Remove room from store
    this.app.$store.commit('chatRooms/DELETE_CHAT_ROOM', roomId);

    // Emit event for components to handle navigation
    emitter.emit(BUS_EVENTS.CHAT_ROOM_DELETED, data);
  };

  onChatRoomMemberAdded = data => {
    // Refresh the room data to get updated members list
    if (data.id) {
      this.app.$store.dispatch('chatRooms/show', data.id);
    }

    // Emit event for components
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MEMBER_ADDED, data);
  };

  onChatRoomMemberRemoved = data => {
    const roomId = data.id;
    const currentUserId = this.app.$store.getters.getCurrentUserID;

    // If current user was removed, delete the room from local store
    if (data.user_id === currentUserId) {
      this.app.$store.commit('chatRooms/DELETE_CHAT_ROOM', roomId);
    } else {
      // Otherwise refresh the room to get updated members
      this.app.$store.dispatch('chatRooms/getMembers', roomId);
    }

    // Emit event for components
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MEMBER_REMOVED, data);
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
